import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/p25.css';

const API = process.env.REACT_APP_BACKEND_URL;
const GOALS = ['Build strength', 'Aerial, pole or bungee', 'Move better and feel good in my body', 'Just curious'];

export default function Welcome25Page() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const directToken = params.get('t');
  const [token, setToken] = useState(directToken || '');
  const [phase, setPhase] = useState(directToken ? 'screens' : 'waiting');
  const [screen, setScreen] = useState(0);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [gcf, setGcf] = useState(false);
  const [ww, setWw] = useState(false);

  useEffect(() => {
    if (directToken) {
      axios.get(`${API}/api/access25/session?t=${directToken}`, { withCredentials: true })
        .then(({ data }) => {
          setFirst(data.first_name || '');
          setLast(data.last_name || '');
          setGcf(Boolean(data.consent_gcf));
          setWw(Boolean(data.consent_ww));
        }).catch(() => {});
      return;
    }
    if (!sessionId) return;
    let tries = 0;
    const poll = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/api/store25/status/${sessionId}`);
        if (data.paid) {
          clearInterval(poll);
          setToken(data.token);
          setFirst(data.first_name || '');
          await axios.get(`${API}/api/access25/session?t=${data.token}`, { withCredentials: true });
          setPhase('screens');
        } else if (tries > 30) { clearInterval(poll); setPhase('failed'); }
      } catch { if (tries > 30) { clearInterval(poll); setPhase('failed'); } }
    }, 2000);
    return () => clearInterval(poll);
  }, [sessionId, directToken]);

  const save = useCallback((payload) => {
    axios.post(`${API}/api/access25/onboarding`, { t: token, ...payload }, { withCredentials: true }).catch(() => {});
  }, [token]);

  const toProduct = () => navigate(`/workouts/access?t=${token}`);

  if (phase === 'waiting') return (
    <div className="p25-shell" data-testid="welcome25-waiting"><div className="p25-card">
      <h1>Finishing your purchase…</h1><p className="p25-sub">One moment — confirming with Stripe.</p>
      <div className="p25-spinner" /></div></div>);

  if (phase === 'failed') return (
    <div className="p25-shell"><div className="p25-card">
      <h1>Almost there</h1>
      <p className="p25-sub">We couldn't confirm the payment yet. Don't worry — your access link is on its way to your email. Check your inbox in a few minutes.</p>
    </div></div>);

  const dots = <div className="p25-dots">{[0,1,2,3,4].map(i => <span key={i} className={i <= screen ? 'on' : ''} />)}</div>;

  return (
    <div className="p25-shell" data-testid="welcome25-screens">
      <div className="p25-card">
        {screen === 0 && (<>
          <h1>25 for 25 is yours.</h1>
          <p className="p25-sub">One minute to set up your spot, then you're in.</p>
          <button className="p25-btn" data-testid="ob-start" onClick={() => { save({ step: 1 }); setScreen(1); }}>Let's go</button>
        </>)}
        {screen === 1 && (<>
          <h1>First, what's your name?</h1>
          <input className="p25-input" placeholder="First name" value={first} onChange={e => setFirst(e.target.value)} data-testid="ob-first" />
          <input className="p25-input" placeholder="Last name" value={last} onChange={e => setLast(e.target.value)} data-testid="ob-last" />
          <button className="p25-btn" data-testid="ob-next-1" onClick={() => { save({ step: 2, first_name: first, last_name: last }); setScreen(2); }}>Next</button>
        </>)}
        {screen === 2 && (<>
          <h1>What are you here for?</h1>
          <div className="p25-opts">
            {GOALS.map(g => (
              <button key={g} className="p25-opt" data-testid={`ob-goal-${GOALS.indexOf(g)}`}
                onClick={() => { save({ step: 3, goal: g }); setScreen(3); }}>{g}</button>
            ))}
          </div>
          <button className="p25-skip" data-testid="ob-skip" onClick={() => { save({ step: 3 }); setScreen(3); }}>Skip</button>
        </>)}
        {screen === 3 && (<>
          <h1>How would you like to hear from us?</h1>
          <label className="p25-check"><input type="checkbox" checked={gcf} onChange={e => setGcf(e.target.checked)} data-testid="ob-consent-gcf" />
            Send me workout tips and updates from GoCalFit (about 2 a month)</label>
          <label className="p25-check"><input type="checkbox" checked={ww} onChange={e => setWw(e.target.checked)} data-testid="ob-consent-ww" />
            Also tell me about WonderWomanhood, our community for women building businesses, families and communities</label>
          <p className="p25-fine">Your access link always comes by email. Unsubscribe any time.</p>
          <button className="p25-btn" data-testid="ob-finish" onClick={() => { save({ step: 4, consent_gcf: gcf, consent_ww: ww, complete: true }); setScreen(4); }}>Finish</button>
        </>)}
        {screen === 4 && (<>
          <h1>You're in{first ? `, ${first}` : ''}.</h1>
          <p className="p25-sub">We just emailed you your personal link. It's how you get back in any time, no password needed. Tip: on your phone, tap Share, then Add to Home Screen.</p>
          <button className="p25-btn" data-testid="ob-enter" onClick={toProduct}>Start 25 for 25</button>
        </>)}
        {dots}
      </div>
    </div>
  );
}
